import proposalKeys from '@/api/queries/proposal/keyFactor';
import { useGetProposal } from '@/api/queries/proposal/useGetProposal';
import AutoSaveForm from '@/ui/components/autoSaveForm/AutoSaveForm';
import { zodResolver } from '@hookform/resolvers/zod';
import { Field, RawInput } from '@payaca/ambrosia';
import { useJob } from '@payaca/store/hooks/appState';
import * as jobsActions from '@payaca/store/jobs/jobsActions';
import { ProposalSummaryMessage } from '@payaca/types/jobTypesV2';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { z } from 'zod/v4';

const MAX_MESSAGE_LENGTH = 125;

const summaryAlertFormSchema = z.object({
  message: z
    .string()
    .max(MAX_MESSAGE_LENGTH, `Keep this to ${MAX_MESSAGE_LENGTH} characters`),
});

type SummaryAlertFormValues = z.infer<typeof summaryAlertFormSchema>;

export type TProps = {
  /** Public id of the Proposal whose summary alert is being edited. */
  proposalId: string;
  className?: string;
};

/**
 * Always-visible editor for the Proposal's payment summary alert — a short
 * message shown beneath the total payable amount in the customer's Proposal.
 *
 * The alert lives on the legacy Job (`summaryMessages`), which is not exposed
 * over GraphQL, so it is read and written through Redux, keyed by the Job's
 * numeric id.
 */
const ProposalSummaryAlert: FC<TProps> = ({ proposalId, className }) => {
  const dispatch = useDispatch();
  const { proposal } = useGetProposal(proposalId);
  const jobId =
    proposal && Number.isFinite(Number(proposal.id))
      ? Number(proposal.id)
      : undefined;
  const job = useJob(jobId);

  // Load the Job into the legacy Redux store so its summary alert can be read
  // and written through the legacy actions.
  useEffect(() => {
    if (jobId) dispatch(jobsActions.requestGetJob(jobId));
  }, [dispatch, jobId]);

  // Gate until the Job has loaded so the form seeds its default from the
  // persisted message rather than an empty placeholder it never updates from.
  if (!jobId || !job) return null;

  return (
    <ProposalSummaryAlertForm
      key={jobId}
      jobId={jobId}
      proposalId={proposalId}
      initialMessage={job.summaryMessages?.[0]?.message ?? ''}
      className={className}
    />
  );
};

/**
 * Seeded auto-saving form for the summary alert. An empty message clears the
 * alert (persisted as `null`); otherwise it is saved as a single alert-level
 * message. Only mounted once the Job has loaded so the field seeds correctly.
 */
const ProposalSummaryAlertForm: FC<{
  jobId: number;
  proposalId: string;
  initialMessage: string;
  className?: string;
}> = ({ jobId, proposalId, initialMessage, className }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const methods = useForm<SummaryAlertFormValues>({
    defaultValues: { message: initialMessage },
    resolver: zodResolver(summaryAlertFormSchema),
    mode: 'onChange',
    // Auto-save validates the whole form; don't yank focus while typing.
    shouldFocusError: false,
  });

  return (
    <AutoSaveForm
      className={className}
      methods={methods}
      // Compare the persisted value so whitespace-only churn doesn't save.
      getComparable={({ message }) => message.trim()}
      onSubmit={({ message }) => {
        const trimmed = message.trim();
        const summaryMessages: ProposalSummaryMessage[] | null = trimmed
          ? [{ level: 'alert', message: trimmed }]
          : null;

        dispatch(
          jobsActions.partialUpdateJob.request({
            jobId,
            data: { summaryMessages },
            callback: () => {
              dispatch(jobsActions.requestGetJob(jobId));
              void queryClient.invalidateQueries({
                queryKey: proposalKeys.proposalDocument(proposalId),
              });
            },
          })
        );
      }}
    >
      <Field name="message">
        <Field.Label>Payment summary alert</Field.Label>
        <Field.HelperText>
          This text shows immediately below the total payable amount in the
          Proposal summary. Leave it empty to remove it.
        </Field.HelperText>
        <RawInput
          maxLength={MAX_MESSAGE_LENGTH}
          {...methods.register('message')}
        />
      </Field>
    </AutoSaveForm>
  );
};

export default ProposalSummaryAlert;
